import { apiRequest, refreshSession, ApiError } from '@scc/lib'
import type { AuthRepository, AgencyInfo, RemoteStore } from '@/data/repositories/types'
import type { Client, User } from '@/modules/shared/types'
import { useSessionStore } from '@/lib/session.store'
import {
  mapApiOrg,
  mapApiClient,
  mapApiWorkspaces,
  type ApiWorkspace,
  type ApiWorkspaceClient,
  type ApiWorkspacesResponse,
} from './workspace-mappers'
import { mapClaimResult, type ApiClaimResponse } from './invitation-mappers'

interface ApiAuthUser {
  userId: string
  email: string
  role: string
  displayName: string | null
  emailVerified: boolean
  needsPassword?: boolean
}

interface AuthResponse {
  token: string
  accessToken: string
  user: ApiAuthUser
}

interface ApiStore {
  id: number
  name: string
  type: number
  currency: string
  role: 'owner' | 'admin' | 'viewer'
  integrations: string[]
  connectionHealth: unknown[]
  isFavorite: boolean
  legacyId: number | null
  onboardingStep: number
  onboardedAt: string | null
  stage?: 'prospect' | 'onboarding' | 'active'
}

export function mapApiUser(apiUser: ApiAuthUser): User {
  return {
    id: apiUser.userId,
    email: apiUser.email,
    name: apiUser.displayName ?? apiUser.email,
    role: apiUser.role === 'admin' ? 'admin' : 'member',
    organizationId: '',
    emailVerified: apiUser.emailVerified,
  }
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function mapStoreToClient(store: ApiStore, orgId: string): Client {
  const onboardingComplete = store.onboardingStep >= 4 || store.onboardedAt !== null
  return {
    id: String(store.id),
    organizationId: orgId,
    name: store.name,
    slug: slugify(store.name),
    platform: 'tiktok',
    onboardingStatus: onboardingComplete ? 'active' : 'onboarding',
    stage: store.stage,
    role: store.role,
    isFavorite: store.isFavorite,
  }
}

function mapStoreToRemote(store: ApiStore): RemoteStore {
  return {
    id: store.id,
    name: store.name,
    currency: store.currency,
    role: store.role,
    isFavorite: store.isFavorite,
    onboardingStep: store.onboardingStep,
    onboardedAt: store.onboardedAt,
  }
}

export function createApiAuthRepository(): AuthRepository {
  return {
    async login(email, password) {
      const result = await apiRequest<AuthResponse>('/auth/login', {
        method: 'POST',
        body: { email, password },
      })
      return { user: mapApiUser(result.user), token: result.token }
    },
    async register(email, password, displayName) {
      const body: Record<string, string> = { email, password }
      if (displayName) body['displayName'] = displayName
      const result = await apiRequest<AuthResponse>('/auth/register', {
        method: 'POST',
        body,
      })
      return { user: mapApiUser(result.user), token: result.token }
    },
    async me() {
      const apiUser = await apiRequest<ApiAuthUser>('/auth/me')
      return mapApiUser(apiUser)
    },
    async refresh() {
      // Routes through the shared, cross-tab-locked refresh — the same one
      // the auto-retry-on-401 path uses — instead of firing its own
      // /auth/refresh request. The refresh cookie is single-use; two
      // independent callers each redeeming it directly is exactly the race
      // that logs a still-valid session out from under the user.
      const result = await refreshSession<{ token: string; user: ApiAuthUser }>()
      if (!result) throw new ApiError(401, 'Session expired')
      return { user: mapApiUser(result.user), token: result.token }
    },
    async logout() {
      await apiRequest<null>('/auth/logout', {
        method: 'POST',
        skipAuthRefresh: true,
      })
    },
    async listAgencies() {
      return apiRequest<AgencyInfo[]>('/agencies')
    },
    async getWorkspaces() {
      const workspaces = await apiRequest<ApiWorkspacesResponse>('/auth/workspaces')
      return mapApiWorkspaces(workspaces)
    },
    async createOrganization(name) {
      const workspace = await apiRequest<ApiWorkspace>('/auth/organizations', {
        method: 'POST',
        body: { name },
      })
      return mapApiOrg(workspace)
    },
    async getClients(orgId) {
      const clients = await apiRequest<ApiWorkspaceClient[]>(
        `/auth/organizations/${encodeURIComponent(orgId)}/clients`,
      )
      return clients.map((c) => mapApiClient(c, orgId))
    },
    async getStores() {
      const stores = await apiRequest<ApiStore[]>('/auth/stores')
      return stores.map(mapStoreToRemote)
    },
    async createStore({ name, currency, stage, agencyId }) {
      const orgId = useSessionStore.getState().user.organizationId
      const body: Record<string, string | number> = { name }
      if (currency) body['currency'] = currency
      if (stage) body['stage'] = stage
      // Without this the store is created with a NULL agencyId and is invisible
      // to the agency's client list — it exists and you own it, but it never
      // shows in the roster and every agency-scoped URL for it 404s.
      if (agencyId !== undefined) body['agencyId'] = agencyId
      const store = await apiRequest<ApiStore>('/auth/stores', {
        method: 'POST',
        body,
      })
      return mapStoreToClient(store, orgId)
    },
    async toggleStoreFavorite(storeId) {
      return apiRequest<{ isFavorite: boolean }>(
        `/auth/stores/${storeId}/favorite`,
        { method: 'PATCH' },
      )
    },
    async deleteStore(storeId) {
      await apiRequest<null>(`/auth/stores/${storeId}`, { method: 'DELETE' })
    },
    async getLaunchPlan(storeId) {
      return apiRequest<{ plan: Record<string, unknown> | null }>(
        `/auth/stores/${storeId}/launch-plan`,
      )
    },
    async setLaunchPlan(storeId, plan) {
      return apiRequest<{ plan: Record<string, unknown> | null }>(
        `/auth/stores/${storeId}/launch-plan`,
        { method: 'PUT', body: { plan } },
      )
    },
    async acceptInvitation(token) {
      return apiRequest<{ storeId: number; storeName: string; role: string; needsPassword?: boolean }>(
        '/auth/invitations/accept',
        { method: 'POST', body: { token } },
      )
    },
    async claimInvitation(token) {
      const res = await apiRequest<ApiClaimResponse>('/auth/invitations/claim', {
        method: 'POST',
        body: { token },
        token: null,
      })
      return mapClaimResult(res, mapApiUser)
    },
    async setupAccount(input) {
      return apiRequest<{ ok: true }>('/auth/setup-account', { method: 'POST', body: input })
    },
    async forgotPassword(email) {
      // Unauthenticated; backend always returns a generic success (anti-enumeration).
      await apiRequest<{ message: string }>('/auth/forgot-password', {
        method: 'POST',
        body: { email },
        token: null,
        skipAuthRefresh: true,
      })
    },
    async resetPassword(token, password) {
      await apiRequest<{ message: string }>('/auth/reset-password', {
        method: 'POST',
        body: { token, password },
        token: null,
        skipAuthRefresh: true,
      })
    },
    async verifyEmail(token) {
      // Public — the token is the proof. Never send a bearer / trigger refresh.
      await apiRequest<{ message: string }>('/auth/verify-email', {
        method: 'POST',
        body: { token },
        token: null,
        skipAuthRefresh: true,
      })
    },
    async confirmEmailChange(token) {
      // Public for the same reason as verifyEmail: the link is usually opened
      // from the new inbox, with no session on that device.
      const data = await apiRequest<{ message: string; email: string }>('/auth/confirm-email-change', {
        method: 'POST',
        body: { token },
        token: null,
        skipAuthRefresh: true,
      })
      return { email: data.email }
    },
    async resendVerification() {
      // Authed — backend resends to the signed-in user (req.userContext).
      await apiRequest<{ message: string }>('/auth/send-verification', { method: 'POST' })
    },
  }
}

export const authRepository = createApiAuthRepository()
